import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Tooltip from '@mui/material/Tooltip';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { thinnestScrollbarStyles } from 'shared/design-system/settings/variables/Scrollbar';
import {
  faChevronDown,
  faChevronRight,
  faClipboardListCheck,
  faSparkles,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  useProgram,
  ProgramQuestionsSection,
} from 'shared/providers/ProgramProvider';
import { UNSECTIONED_SECTION_KEY } from 'shared/providers/ProgramProvider/utils';
import StatusIcon from './StatusIcon';
import { feedbackSectionIdToString } from '../../NPOFeedback/utils';
import { getSectionIcon } from 'shared/components/WelcomeBack/Sections';
import { FeedbackSectionId } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { mergeClassNames } from 'shared/utils/mergeClassNames';
import { WIZARD_THEMES, WizardThemeKey } from './WizardHeader';
import { isPrefilledFromProgram } from './prefill';

const PREFILLED_TOOLTIP =
  'Your program profile already has an answer for this question. Open it and press Save to include the answer in this report.';

type SectionExpandedKey = FeedbackSectionId | typeof UNSECTIONED_SECTION_KEY;
const expandedKey = (s: ProgramQuestionsSection): SectionExpandedKey =>
  s.section ?? UNSECTIONED_SECTION_KEY;

const Wrapper = styled.aside<{
  accent: string;
  accentBackground: string;
  backgroundColor?: keyof typeof COLORS;
}>`
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${COLORS.grayLight};
  ${p => p.backgroundColor && `background-color: ${COLORS[p.backgroundColor]};`}
  width: 100%;
  height: 100%;
  overflow: auto;
  ${thinnestScrollbarStyles}

  .section-list {
    display: flex;
    flex-direction: column;
    padding: ${SPACERS.tiny} 0 ${SPACERS.medium};
  }

  .section {
    display: flex;
    flex-direction: column;
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: ${SPACERS.tiny};
    padding: ${SPACERS.small} ${SPACERS.medium};
    cursor: pointer;
    transition: background-color 0.3s ease;
    &:hover {
      background-color: ${p => p.accent}0D;
    }
    .section-title {
      flex: 1;
      color: ${COLORS.greyDarkest};
      font-size: 0.875rem;
    }
    .section-counter {
      color: ${COLORS.greyDark};
      white-space: nowrap;
    }
    .section-icon {
      color: ${COLORS.greyDark};
    }
    .section-status {
      flex-shrink: 0;
    }
    .section-chevron {
      flex-shrink: 0;
      width: 10px;
      height: 10px;
      color: ${COLORS.greyDark};
      transition: transform 0.2s ease;
    }
    &.collapsed .section-chevron {
      transform: rotate(-90deg);
    }
  }

  /* Collapse animation: grid-template-rows 1fr -> 0fr animates the list
     open/closed without measuring heights in JS. */
  .question-list-wrap {
    display: grid;
    grid-template-rows: 1fr;
    transition: grid-template-rows 0.25s ease;
    &.closed {
      grid-template-rows: 0fr;
    }
  }

  .question-list {
    display: flex;
    flex-direction: column;
    padding-bottom: ${SPACERS.tiny};
    min-height: 0;
    overflow: hidden;
  }

  .question-list-wrap.closed .question-list {
    padding-bottom: 0;
  }

  .question-row {
    display: flex;
    align-items: center;
    gap: ${SPACERS.tiny};
    padding: ${SPACERS.tiny} ${SPACERS.medium} ${SPACERS.tiny}
      calc(${SPACERS.medium} + ${SPACERS.small});
    cursor: pointer;
    border-left: 3px solid transparent;
    transition: background-color 0.3s ease;
    &:hover {
      background-color: ${p => p.accent}0D;
    }
    &.active {
      background-color: ${p => p.accent}14;
      border-left-color: ${p => p.accent};
      .question-label {
        font-weight: 700;
        color: ${COLORS.greyDarkest};
      }
    }
    .question-label {
      flex: 1;
      color: ${COLORS.greyDark};
      line-height: 1.35;
    }
    .question-status {
      flex-shrink: 0;
    }

    &.prefilled {
      background-color: ${COLORS.successBackground}66;
      &:hover {
        background-color: ${COLORS.successBackground};
      }
      .question-label {
        color: ${COLORS.greyDarkest};
      }
    }

    .prefilled-badge {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      gap: 3px;
      padding: 2px 7px 2px 5px;
      border-radius: 999px;
      background-color: ${COLORS.successBackground};
      border: 1px solid ${COLORS.successSubdued};
      color: ${COLORS.success};
      font-size: 0.625rem;
      font-weight: 600;
      line-height: 1;
      white-space: nowrap;
      letter-spacing: 0.02em;
      text-transform: uppercase;
      cursor: help;
      transition: background-color 0.2s ease, border-color 0.2s ease;
      svg {
        width: 9px;
        height: 9px;
      }
      &:hover {
        background-color: ${COLORS.whiteBase};
        border-color: ${COLORS.success};
      }
    }
  }

  .review-row {
    display: flex;
    align-items: center;
    gap: ${SPACERS.tiny};
    padding: ${SPACERS.small} ${SPACERS.medium};
    cursor: pointer;
    border-left: 3px solid transparent;
    transition: background-color 0.3s ease;
    &:hover {
      background-color: ${p => p.accent}0D;
    }
    &.active {
      background-color: ${p => p.accent}14;
      border-left-color: ${p => p.accent};
      .review-label {
        color: ${COLORS.greyDarkest};
      }
    }
    .review-chevron {
      flex-shrink: 0;
      width: 10px;
      height: 10px;
      color: ${COLORS.greyDark};
    }
    .review-label {
      flex: 1;
      color: ${COLORS.greyDarkest};
    }
    .review-icon {
      color: ${COLORS.greyDark};
    }
  }
`;

interface ProgramSidebarProps {
  onOpenReview: () => void;
  isReviewActive: boolean;
  onSelectQuestion: (questionId: number) => void;
  /**
   * Visual identity: 'snapshot' (blue) vs 'program' (orange). Defaults
   * to the historical blue treatment so any legacy callers keep working.
   */
  theme?: WizardThemeKey;
  /**
   * Optional identity card rendered at the top of the sidebar list.
   * Scrolls with the sections (non-sticky) so it never steals space from
   * the right-hand editing pane. Callers usually pass `<SidebarIdentity />`.
   */
  identity?: React.ReactNode;
  backgroundColor?: keyof typeof COLORS;
}

const ProgramSidebar: React.FC<ProgramSidebarProps> = ({
  onOpenReview,
  isReviewActive,
  onSelectQuestion,
  theme = 'snapshot',
  identity,
  backgroundColor,
}) => {
  const tokens = WIZARD_THEMES[theme];
  const {
    questions,
    activeQuestionId,
    sectionStatus,
    questionStatus,
    getProgramProfileAnswer,
  } = useProgram();

  const [expanded, setExpanded] = useState<
    Partial<Record<SectionExpandedKey, boolean>>
  >({});

  const sections =
    questions.type === 'REQUEST_SUCCESS' ? questions.data.sections : [];

  // Auto-expand the section containing the active question.
  useEffect(() => {
    if (activeQuestionId == null) return;
    for (const s of sections) {
      if (s.questions.some(q => q.questionId === activeQuestionId)) {
        setExpanded(prev => ({ ...prev, [expandedKey(s)]: true }));
        break;
      }
    }
  }, [activeQuestionId, sections]);

  const toggleSection = (s: ProgramQuestionsSection) => {
    const key = expandedKey(s);
    setExpanded(prev => ({
      ...prev,
      [key]: !(prev[key] ?? true),
    }));
  };

  return (
    <Wrapper
      accent={tokens.accent}
      accentBackground={tokens.accentBackground}
      backgroundColor={backgroundColor}
    >
      {identity}

      <div className="section-list">
        {sections.map(s => {
          const key = expandedKey(s);
          const label = s.section
            ? feedbackSectionIdToString(s.section)
            : 'Uncategorized';
          const isOpen = expanded[key] ?? true;
          return (
            <div className="section" key={key}>
              <div
                className={mergeClassNames(
                  'section-header',
                  !isOpen && 'collapsed',
                )}
                onClick={() => toggleSection(s)}
                role="button"
                aria-expanded={isOpen}
              >
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className="section-chevron"
                />
                {s.section && (
                  <FontAwesomeIcon
                    icon={getSectionIcon(s.section)}
                    className="section-icon"
                  />
                )}
                <TrueImpactText
                  variant="body-small-bold"
                  className="section-title"
                >
                  {label}
                </TrueImpactText>
                <TrueImpactText
                  variant="body-small"
                  className="section-counter"
                >
                  {s.answered}/{s.total}
                </TrueImpactText>
                <StatusIcon
                  status={sectionStatus(s)}
                  size={16}
                  className="section-status"
                />
              </div>
              <div
                className={mergeClassNames(
                  'question-list-wrap',
                  !isOpen && 'closed',
                )}
                aria-hidden={!isOpen}
              >
                <div className="question-list">
                  {s.questions.map(q => {
                    const prefilled = isPrefilledFromProgram(
                      q,
                      getProgramProfileAnswer,
                    );
                    return (
                      <div
                        key={q.questionId}
                        className={mergeClassNames(
                          'question-row',
                          !isReviewActive &&
                            activeQuestionId === q.questionId &&
                            'active',
                          prefilled && 'prefilled',
                        )}
                        onClick={() => onSelectQuestion(q.questionId)}
                        role="button"
                        tabIndex={isOpen ? 0 : -1}
                      >
                        <TrueImpactText
                          variant="body-small"
                          className="question-label"
                        >
                          {q.name || q.text || `Question ${q.questionId}`}
                        </TrueImpactText>
                        {prefilled ? (
                          <Tooltip
                            arrow
                            placement="right"
                            title={PREFILLED_TOOLTIP}
                          >
                            <span className="prefilled-badge">
                              <FontAwesomeIcon icon={faSparkles} />
                              Prefilled
                            </span>
                          </Tooltip>
                        ) : (
                          <StatusIcon
                            status={questionStatus(q)}
                            size={14}
                            className="question-status"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}

        <div
          className={mergeClassNames('review-row', isReviewActive && 'active')}
          onClick={onOpenReview}
          role="button"
          aria-pressed={isReviewActive}
        >
          <FontAwesomeIcon
            icon={faChevronRight}
            className="review-chevron"
          />
          <FontAwesomeIcon
            icon={faClipboardListCheck}
            className="review-icon"
          />
          <TrueImpactText variant="body-small-bold" className="review-label">
            Review & Submit
          </TrueImpactText>
        </div>
      </div>
    </Wrapper>
  );
};

export default ProgramSidebar;
